/**
 * Botón "Agregar al calendario" con menú desplegable.
 * Permite exportar una cita a Google Calendar, Outlook.com o descargar .ics.
 * @module components/appointments/AddToCalendarButton
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { CalendarIcon } from '@heroicons/react/24/outline';
import { Button } from '@/components/ui/button';
import type { Appointment } from '@/types/appointment.types';
import {
  getGoogleCalendarUrl,
  getOutlookCalendarUrl,
  downloadIcsFile,
  type CalendarEventData,
} from '@/utils/addToCalendar';

interface AddToCalendarButtonProps {
  appointment: Appointment;
  /** Variante del botón: "default" muestra el botón completo, "compact" solo icono */
  variant?: 'default' | 'compact';
}

/**
 * Construye el objeto CalendarEventData a partir de una cita.
 */
function appointmentToCalendarEvent(appointment: Appointment): CalendarEventData {
  const startDate = new Date(appointment.scheduled_at);
  const endDate = new Date(startDate.getTime() + appointment.duration * 60_000);

  const descriptionParts: string[] = [];
  if (appointment.contact_name) {
    descriptionParts.push(`Contacto: ${appointment.contact_name}`);
  }
  if (appointment.agent_name) {
    descriptionParts.push(`Agente: ${appointment.agent_name}`);
  }
  if (appointment.delegation_name) {
    descriptionParts.push(`Delegación: ${appointment.delegation_name}`);
  }
  if (appointment.notes) {
    descriptionParts.push(`\nNotas: ${appointment.notes}`);
  }

  const title = appointment.contact_name
    ? `Cita Migro - ${appointment.contact_name}`
    : 'Cita Migro';

  return {
    title,
    startDate,
    endDate,
    description: descriptionParts.length > 0 ? descriptionParts.join('\n') : undefined,
    location: appointment.location || appointment.delegation_name || undefined,
  };
}

const MENU_OPTIONS = [
  {
    id: 'google',
    label: 'Google Calendar',
    icon: (
      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M18 3H6a3 3 0 00-3 3v12a3 3 0 003 3h12a3 3 0 003-3V6a3 3 0 00-3-3z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M16 2v2M8 2v2M3 10h18M8 14h.01M12 14h.01M16 14h.01M8 18h.01M12 18h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
  },
  {
    id: 'outlook',
    label: 'Outlook',
    icon: (
      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <rect x="3" y="4" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="2"/>
        <path d="M3 10h18M9 4v16" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
      </svg>
    ),
  },
  {
    id: 'ics',
    label: 'Descargar .ics',
    icon: (
      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
  },
] as const;

export const AddToCalendarButton: React.FC<AddToCalendarButtonProps> = ({
  appointment,
  variant = 'default',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Cerrar menú al hacer clic fuera
  useEffect(() => {
    if (!isOpen) return;

    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Cerrar menú con Escape
  useEffect(() => {
    if (!isOpen) return;

    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    }

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  const handleOptionClick = useCallback(
    (optionId: string) => {
      const event = appointmentToCalendarEvent(appointment);

      switch (optionId) {
        case 'google': {
          const url = getGoogleCalendarUrl(event);
          window.open(url, '_blank', 'noopener,noreferrer');
          break;
        }
        case 'outlook': {
          const url = getOutlookCalendarUrl(event);
          window.open(url, '_blank', 'noopener,noreferrer');
          break;
        }
        case 'ics': {
          downloadIcsFile(event);
          break;
        }
      }

      setIsOpen(false);
    },
    [appointment],
  );

  return (
    <div className="relative" ref={menuRef}>
      <Button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        size={variant === 'compact' ? 'sm' : 'default'}
        className={
          variant === 'compact'
            ? 'h-9 w-9 p-0 bg-green-600 hover:bg-green-700 text-white'
            : 'gap-2 bg-green-600 hover:bg-green-700 text-white'
        }
        aria-haspopup="true"
        aria-expanded={isOpen}
        aria-label="Agregar al calendario"
      >
        <CalendarIcon className={variant === 'compact' ? 'h-4 w-4' : 'h-5 w-5'} />
        {variant !== 'compact' && <span>Agregar al calendario</span>}
      </Button>

      {isOpen && (
        <div
          className="absolute right-0 z-10 mt-2 w-52 origin-top-right rounded-lg border border-gray-200 bg-white shadow-lg ring-1 ring-black/5 focus:outline-none"
          role="menu"
          aria-orientation="vertical"
          aria-label="Opciones de calendario"
        >
          <div className="py-1">
            {MENU_OPTIONS.map((option) => (
              <button
                key={option.id}
                type="button"
                onClick={() => handleOptionClick(option.id)}
                className="flex w-full items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-700 transition-colors focus-visible:outline-none focus-visible:bg-blue-50 focus-visible:text-blue-700"
                role="menuitem"
              >
                {option.icon}
                {option.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
